import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import {View, FlatList, ActivityIndicator, TextInput, TouchableOpacity, Dimensions} from 'react-native';
import EStyleSheet from "react-native-extended-stylesheet";
import {useDebouncedCallback} from 'use-debounce';
import {CancelToken} from "axios";
import {CommonActions} from '@react-navigation/native';
import {Clipboard} from '@react-native-community/clipboard';

import AppText from "../../../components/AppText";
import MessagesService from "../../../services/MessagesService";
import Avatar from "../../../components/Avatar";
import {AVATAR_MESSENGER} from "../../../config";
import {basicStyles} from "../../../styles/basic";
import PencilSvg from "../../../components/svgs/PencilSvg";
import StarRangeInputField from "../../../components/StarRangeInputField";
import {formatDate, formatPhoneNumber, getUserDisplayName} from "../../../utilities/methods";
import AppButton from "../../../components/AppButton";
import LinkButton from "../../../components/LinkButton";
import ChevronLeftBlueSvg from "../../../components/svgs/ChevronLeftBlueSvg";
import {inputStyles} from "../../../styles/input";
import AppPickerInput from "../../../components/AppPickerInput";
import UsersService from "../../../services/UsersService";
import {connect} from 'react-redux';
import {useDarkMode} from "../DarkModeContext";
import CopySvg from "../../../components/svgs/CopySvg";
import CheckMarkSvg from "../../../components/svgs/CheckMarkSvg";

function MessageFeedCustomerInfo({organizationId, messageFeedId, setBlockModalActive, messageFeedBlocked, updateSelectedMessageInList, updateMessageFeed, updateFeedWithExistingData, setCustomerInfoActive, ...props}) {
    const {isDarkMode, theme} = useDarkMode();
    const [initialized, setInitialized] = useState(false);
    const [messageFeed, setMessageFeed] = useState({});
    const [contactNameInputFocused, setContactNameInputFocused] = useState(false);
    const [contactNameInputHovered, setContactNameInputHovered] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [page, setPage] = useState(0);
    const [isLastPage, setIsLastPage] = useState(false);
    const [jobNotesExpanded, setJobNotesExpanded] = useState([]);
    const [contactCopied, setContactCopied] = useState(false);
    const perPage = 20;
    const fetchDataRequestSource = useRef(null);
    const updateDataRequestSource = useRef(null);
    const copyConfirmationTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);

    const contactValue = messageFeed.message_feed_phone_number
        ? formatPhoneNumber(messageFeed.message_feed_phone_number) || messageFeed.message_feed_phone_number
        : messageFeed.message_feed_email_address;

    const screenWidth = useMemo(() => {
        return Dimensions.get('window').width;
    }, [])

    const updateMessageFeedInformation = useCallback(() => {
        // cancel any pending request
        if (updateDataRequestSource.current) {
            updateDataRequestSource.current.cancel('Request Canceled1');
        }
        // create new cancel token
        updateDataRequestSource.current = CancelToken.source();

        MessagesService.updateMessageFeed(organizationId, messageFeedId, messageFeed, updateDataRequestSource.current?.token)
            .then(({messageFeed: serverFeed, messages: serverMessages}) => {
                if (!isMountedRef.current) {
                    return;
                }
                // Use updateFeedWithExistingData to preserve existing statuses, notes, and assignments
                updateFeedWithExistingData(serverFeed, serverMessages);
                setMessageFeed(currentMsgFeed => ({...serverFeed, message_feed_contact_name: currentMsgFeed.message_feed_contact_name, message_feed_assigned_user_organization_id: currentMsgFeed.message_feed_assigned_user_organization_id}));
            }).catch(e => {
                if (!e.__CANCEL__) {
                    console.log('Error fetching message feeds: ', e);
                }
            });
    }, [messageFeedId, organizationId, messageFeed, updateFeedWithExistingData]);

    const [updateMessageFeedDebounce] = useDebouncedCallback(() => {
        updateMessageFeedInformation();
    }, 500);

    const [messageFeedUsers, setMessageFeedUsers] = useState([]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;

            if (fetchDataRequestSource.current) {
                fetchDataRequestSource.current.cancel('Request Canceled2');
            }
            if (updateDataRequestSource.current) {
                updateDataRequestSource.current.cancel('Request Canceled3');
            }

            if (updateMessageFeedDebounce?.cancel) {
                updateMessageFeedDebounce.cancel();
            }
            if (copyConfirmationTimeoutRef.current) {
                clearTimeout(copyConfirmationTimeoutRef.current);
            }
        };
    }, [updateMessageFeedDebounce]);

    useEffect(() => {
        MessagesService.getMessageFeed(organizationId, messageFeedId)
            .then(({messageFeed}) => {
                if (!isMountedRef.current) {
                    return;
                }
                setMessageFeed(messageFeed);

                if (!messageFeed.message_feed_assigned_user_organization_id) {
                    setMessageFeed({
                        ...messageFeed,
                        message_feed_assigned_user_organization_id: props.auth.user.user_organization_id
                    });
                }
            });
    }, [messageFeedId, organizationId]);

    useEffect(() => {
        UsersService.getMessageflowUsers(organizationId)
            .then((data) => {
                if (!isMountedRef.current) {
                    return;
                }
                const {users, storeFront} = data;
                if (storeFront) {
                    setMessageFeedUsers([
                        {
                            ...storeFront,
                            profile_first_name: storeFront['store_front_title'],
                            user_organization_id: 'Default Store Front'
                        },
                        ...users
                    ]);

                } else {
                    setMessageFeedUsers(users);
                }
            });
    }, [organizationId]);

    useEffect(() => {
        // cancel any pending request
        if (fetchDataRequestSource.current) {
            fetchDataRequestSource.current.cancel('Request Canceled4');
        }
        // create new cancel token
        fetchDataRequestSource.current = CancelToken.source();

        MessagesService.getMessageFeedJobs(organizationId, messageFeedId, page, perPage, fetchDataRequestSource.current?.token)
            .then(jobs => {
                if (!isMountedRef.current) {
                    return;
                }
                setIsLastPage(jobs.length < perPage);
                setJobs(current => {
                    return [
                        ...(page === 0 ? [] : current),
                        ...jobs
                    ];
                });
            }).catch(e => {
            if (!e.__CANCEL__) {
                console.log('Error fetching message feeds: ', e);
            }
        });

        return () => {
            if (fetchDataRequestSource.current) {
                fetchDataRequestSource.current.cancel('Request Canceled5');
            }
        };
    }, [organizationId, messageFeedId, page]);


    useEffect(() => {
        setInitialized(false);
        setContactCopied(false);
        if (copyConfirmationTimeoutRef.current) {
            clearTimeout(copyConfirmationTimeoutRef.current);
            copyConfirmationTimeoutRef.current = null;
        }
    }, [messageFeedId, setInitialized]);

    useEffect(() => {
        if(initialized) {
            updateMessageFeedDebounce();
        } else if (messageFeed.hasOwnProperty('message_feed_contact_name')) {
            setInitialized(true);
        }
    }, [messageFeed.message_feed_assigned_user_organization_id, messageFeed.message_feed_contact_name]);

    if (messageFeed.message_feed_id) {
        return (
            <View style={[basicStyles.flexScale, isDarkMode && {backgroundColor: theme.background}]}>
                <View style={[styles.customerInfoTop, isDarkMode && {borderBottomColor: theme.border}]}>
                    <Avatar
                        source={{uri: AVATAR_MESSENGER}}
                        width={40}
                        style={{marginRight: 20}}
                    />
                    <View style={[basicStyles.flexScale]}>
                        {
                            ((contactNameInputHovered || screenWidth <= 1000) && !contactNameInputFocused )?
                                <PencilSvg style={styles.pencilIcon}/>
                                : null
                        }
                        <TextInput
                            style={[styles.contactNameTextInput, contactNameInputFocused ? styles.contactNameTextInputFocused : {}, isDarkMode && {color: theme.text}]}
                            placeholderTextColor={isDarkMode ? theme.placeholderColor : undefined}
                            placeholder="Contact Name"
                            value={messageFeed.message_feed_contact_name || ''}
                            onChangeText={value => {
                                setMessageFeed(current => {
                                    return {
                                        ...current,
                                        message_feed_contact_name: value
                                    };
                                });
                            }}
                            onFocus={() => {
                                setContactNameInputFocused(true);
                            }}
                            onBlur={() => {
                                setContactNameInputFocused(false);
                            }}
                            onMouseEnter={() => {
                                setContactNameInputHovered(true);
                            }}
                            onMouseLeave={() => {
                                setContactNameInputHovered(false);
                            }}
                        />
                        <View style={styles.contactValueWrapper}>
                            <AppText style={[styles.contactPhoneNumber, isDarkMode && {color: theme.contactPhoneColor}]}>
                                {contactValue}
                            </AppText>
                            {contactValue ?
                                <TouchableOpacity
                                    accessibilityLabel={contactCopied ? 'Copied to clipboard' : `Copy ${messageFeed.message_feed_phone_number ? 'phone number' : 'email address'}`}
                                    accessibilityRole="button"
                                    hitSlop={{top: 8, right: 8, bottom: 8, left: 8}}
                                    onPress={() => {
                                        Clipboard.setString(contactValue);
                                        setContactCopied(true);
                                        if (copyConfirmationTimeoutRef.current) {
                                            clearTimeout(copyConfirmationTimeoutRef.current);
                                        }
                                        copyConfirmationTimeoutRef.current = setTimeout(() => {
                                            setContactCopied(false);
                                        }, 2000);
                                    }}
                                    style={[styles.copyContactButton, contactCopied && styles.copyContactButtonCopied]}
                                >
                                    {contactCopied ?
                                        <>
                                            <CheckMarkSvg fill="#00875A"/>
                                            <AppText accessibilityLiveRegion="polite" style={styles.copyConfirmationText}>Copied!</AppText>
                                        </>
                                        : <CopySvg color={isDarkMode ? theme.contactPhoneColor : '#0065DB'}/>
                                    }
                                </TouchableOpacity>
                                : null
                            }
                        </View>
                    </View>
                </View>
                <TouchableOpacity
                    style={styles.backButton}
                    onPress={() => {
                    setCustomerInfoActive(false);
                }}
                >
                    <ChevronLeftBlueSvg/>
                    <AppText style={styles.backButtonText}>
                        Back
                    </AppText>
                </TouchableOpacity>
                <View style={[{paddingTop: 10, paddingBottom: 10, paddingRight: 15, paddingLeft: 15}]}>
                    <AppPickerInput
                        label="Task Assigned To"
                        onValueChange={value => setMessageFeed(current => {
                            return {
                                ...current,
                                message_feed_assigned_user_organization_id: value
                            };
                        })
                        }
                        selectedValue={messageFeed.message_feed_assigned_user_organization_id?.toString()}
                        items={messageFeedUsers}
                        labelExtractor={(user) => getUserDisplayName(user)}
                        valueExtractor={(user) => user.user_organization_id.toString()}
                        wrapperStyle={isDarkMode ? {backgroundColor: theme.inputBg, borderColor: theme.inputBorder} : {}}
                        labelStyle={isDarkMode ? {color: theme.textSecondary} : {}}
                        inputTextStyle={isDarkMode ? {color: theme.text} : {}}
                        arrowColor={isDarkMode ? theme.text : '#0065DB'}
                        arrowBorderColor={isDarkMode ? theme.inputBorder : '#E2E9F4'}
                    />
                </View>
                <AppText style={[styles.feedBackHeading, isDarkMode && {color: theme.text}]}>
                    Job History
                </AppText>
                {
                    jobs.length ?
                    <FlatList
                        style={[basicStyles.flexScale]}
                        data={jobs}
                        keyExtractor={({job_id}) => job_id}
                        renderItem={({item: job}) => {
                            return (
                                <View style={[styles.jobContainer]}>
                                    <LinkButton action={() => {
                                        return CommonActions.navigate('Jobs', {
                                            screen: 'JobIndexEditNavigation',
                                            params: {
                                                screen: 'JobEdit',
                                                params: {
                                                    id: job.job_id,
                                                },
                                            },
                                        });
                                    }}>
                                        <View
                                            style={[
                                                inputStyles.inputWrapper,
                                                basicStyles.flexRow,
                                                basicStyles.alignContentCenter,
                                                styles.jobFieldWrapper,
                                            ]}>
                                            <View style={basicStyles.flexScale}>
                                                <AppText style={[inputStyles.inputLabel, styles.jobFieldLabel, isDarkMode && {color: theme.textSecondary}]}>
                                                    Job ID
                                                </AppText>
                                                <AppText style={[inputStyles.input, styles.jobFieldContent, isDarkMode && {color: theme.text}]} numberOfLines={1}>
                                                    {job.job_number}
                                                </AppText>
                                            </View>
                                        </View>
                                    </LinkButton>
                                    <View
                                        style={[
                                            inputStyles.inputWrapper,
                                            basicStyles.flexRow,
                                            basicStyles.alignContentCenter,
                                            styles.jobFieldWrapper,
                                        ]}>
                                        <View style={basicStyles.flexScale}>
                                            <AppText style={[inputStyles.inputLabel, styles.jobFieldLabel, isDarkMode && {color: theme.textSecondary}]}>
                                                Date
                                            </AppText>
                                            <AppText style={[inputStyles.input, styles.jobFieldContent, isDarkMode && {color: theme.text}]} numberOfLines={1}>
                                                {formatDate(job.job_timestamp)}
                                            </AppText>
                                        </View>
                                    </View>
                                    <View
                                        style={[
                                            inputStyles.inputWrapper,
                                            basicStyles.flexRow,
                                            basicStyles.alignContentCenter,
                                            styles.jobFieldWrapper,
                                        ]}>
                                        <View style={basicStyles.flexScale}>
                                            <AppText style={[inputStyles.inputLabel, styles.jobFieldLabel, isDarkMode && {color: theme.textSecondary}]}>
                                                Product Type
                                            </AppText>
                                            <AppText style={[inputStyles.input, styles.jobFieldContent, isDarkMode && {color: theme.text}]} numberOfLines={1}>
                                                {job.job_product_type}
                                            </AppText>
                                        </View>
                                    </View>
                                    <View
                                        style={[
                                            inputStyles.inputWrapper,
                                            basicStyles.flexRow,
                                            basicStyles.alignContentCenter,
                                            styles.jobFieldWrapper,
                                        ]}>
                                        <View style={basicStyles.flexScale}>
                                            <AppText style={[inputStyles.inputLabel, styles.jobFieldLabel, isDarkMode && {color: theme.textSecondary}]}>
                                                Product Brand
                                            </AppText>
                                            <AppText style={[inputStyles.input, styles.jobFieldContent, isDarkMode && {color: theme.text}]} numberOfLines={1}>
                                                {job.job_product_brand}
                                            </AppText>
                                        </View>
                                    </View>
                                    {
                                        !jobNotesExpanded.includes(job.job_number) && job.jobNumberJobNotes.length > 0 && job.jobNumberJobNotes[0].job_notes ?
                                            <TouchableOpacity onPress={() => {
                                                setJobNotesExpanded(current => {
                                                    current.push(job.job_number);
                                                        return [...current];
                                                })
                                            }}>
                                                <View
                                                    style={[
                                                        inputStyles.inputWrapper,
                                                        basicStyles.flexRow,
                                                        basicStyles.alignContentCenter,
                                                        styles.jobFieldWrapper,
                                                    ]}>
                                                    <View style={basicStyles.flexScale}>
                                                        <AppText style={[inputStyles.inputLabel, styles.jobFieldLabel, isDarkMode && {color: theme.textSecondary}]}>
                                                            Note @ {formatDate(job.jobNumberJobNotes[0].job_timestamp, 'MM/DD/YYYY hh:mm')}
                                                        </AppText>
                                                        <AppText style={[inputStyles.input, styles.jobFieldContent, isDarkMode && {color: theme.text}]} numberOfLines={1}>
                                                            {job.jobNumberJobNotes[0].job_notes}
                                                        </AppText>
                                                    </View>
                                                </View>
                                            </TouchableOpacity>
                                            :
                                        null
                                    }
                                    {
                                        jobNotesExpanded.includes(job.job_number) ?

                                            <View
                                                style={[
                                                    inputStyles.inputWrapper,
                                                    basicStyles.alignContentCenter,
                                                    styles.jobFieldWrapper,
                                                ]}>
                                                {
                                                    job.jobNumberJobNotes.map(jobNote => {
                                                        return (
                                                            <View
                                                                key={jobNote.job_id}
                                                                style={[basicStyles.flexScale, styles.jobNotesInnerWrapper]}
                                                            >
                                                                <AppText
                                                                    style={[inputStyles.inputLabel, styles.jobFieldLabel, isDarkMode && {color: theme.textSecondary}]}>
                                                                    Note @ {formatDate(job.jobNumberJobNotes[0].job_timestamp, 'MM/DD/YYYY hh:mm')}
                                                                </AppText>
                                                                <AppText
                                                                    style={[inputStyles.input, styles.jobFieldContent, isDarkMode && {color: theme.text}]}>
                                                                    {jobNote.job_notes}
                                                                </AppText>
                                                            </View>
                                                        );
                                                    })
                                                }
                                            </View>
                                            : null
                                    }
                                    {
                                        job.review_timestamp ?
                                            <>
                                                <StarRangeInputField
                                                    wrapperStyle={[styles.jobFieldWrapper]}
                                                    labelStyle={[styles.jobFieldLabel, isDarkMode && {color: theme.textSecondary}]}
                                                    inputStyle={[styles.jobFieldContent, isDarkMode && {color: theme.text}]}
                                                    label="Overall Rating"
                                                    value={job.review_rating}
                                                    disabled={true}
                                                />
                                                <View
                                                    style={[
                                                        inputStyles.inputWrapper,
                                                        basicStyles.flexRow,
                                                        basicStyles.alignContentCenter,
                                                        styles.jobFieldWrapper,
                                                    ]}>
                                                    <View style={basicStyles.flexScale}>
                                                        <AppText style={[inputStyles.inputLabel, styles.jobFieldLabel, isDarkMode && {color: theme.textSecondary}]}>
                                                            Review
                                                        </AppText>
                                                        <AppText style={[inputStyles.input, styles.jobFieldContent, isDarkMode && {color: theme.text}]} numberOfLines={1}>
                                                            {job.review_text}
                                                        </AppText>
                                                    </View>
                                                </View>
                                                <View
                                                    style={[
                                                        inputStyles.inputWrapper,
                                                        basicStyles.flexRow,
                                                        basicStyles.alignContentCenter,
                                                        styles.jobFieldWrapper,
                                                    ]}>
                                                    <View style={basicStyles.flexScale}>
                                                        <AppText style={[inputStyles.inputLabel, styles.jobFieldLabel, isDarkMode && {color: theme.textSecondary}]}>
                                                            Review Response
                                                        </AppText>
                                                        <AppText style={[inputStyles.input, styles.jobFieldContent, isDarkMode && {color: theme.text}]} numberOfLines={1}>
                                                            {job.client_response || 'No Feedback Yet'}
                                                        </AppText>
                                                    </View>
                                                </View>
                                            </>
                                            : null
                                    }
                                </View>
                            );
                        }}
                        onEndReachedThreshold={0.1}
                        onEndReached={() => {
                            if (!isLastPage) {
                                setPage(current => current + 1);
                            }
                        }}
                    />
                        :
                        <View style={[basicStyles.flexScale]}>
                            <AppText style={[styles.noJobsText, isDarkMode && {color: theme.textSecondary}]}>
                                No Jobs Found For Contact
                            </AppText>
                        </View>
                }
                <View style={[styles.blockContactContainer, isDarkMode && {borderTopWidth: 1, borderTopColor: theme.border}]}>
                    <AppButton
                        label={`${messageFeedBlocked ? 'Unblock' : 'Block'} Contact`}
                        theme="transRed"
                        action={() => {
                            setBlockModalActive(true);
                        }}
                        style={isDarkMode ? {backgroundColor: 'transparent', borderColor: '#EA0024'} : {}}
                        textStyle={isDarkMode ? {color: '#EA0024'} : {}}
                    />
                </View>
            </View>
        );
    }

    return (
        <View style={[basicStyles.flexCenterContent, basicStyles.flexScale]}>
            <ActivityIndicator size="large"/>
        </View>
    );
}

const mapStateToProps = (state) => {
    const {auth} = state;
    return {auth};
};

export default connect(mapStateToProps)(MessageFeedCustomerInfo);

const styles = EStyleSheet.create({
    customerInfoTop: {
        padding: 15,
        flexDirection: 'row',
        alignItems: 'flex-start',
        borderBottomWidth: 1,
        borderBottomColor: '#E8EDF6',
    },
    contactNameTextInput: {
        fontSize: 16,
        width: '100%',
        marginBottom: 3,
        fontFamily: 'SourceSansPro-SemiBold',
        cursor: 'pointer',
    },
    contactNameTextInputFocused: {
        cursor: 'auto'
    },
    contactPhoneNumber: {
        fontSize: 16,
        fontFamily: 'SourceSansPro-SemiBold',
        textDecorationLine: "underline",
        textDecorationStyle: "solid",
    },
    contactValueWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    copyContactButton: {
        marginLeft: 8,
        padding: 2,
        flexDirection: 'row',
        alignItems: 'center',
    },
    copyContactButtonCopied: {
        paddingRight: 6,
    },
    copyConfirmationText: {
        color: '#00875A',
        fontSize: 12,
        fontFamily: 'SourceSansPro-SemiBold',
        marginLeft: 4,
    },
    pencilIcon: {
        position: 'absolute',
        top: 3,
        left: -13
    },
    jobContainer: {
        paddingRight: 15,
        paddingLeft: 15,
        marginBottom: 24,
    },
    jobFieldWrapper: {
        backgroundColor: 'transparent',
        marginBottom: 3,
        padding: 12,
        height: 'auto',
        minHeight: 'auto'
    },
    jobNotesInnerWrapper: {
        minHeight: 'auto', //idk why but this is needed or text can cutoff
        marginBottom: 15
    },
    jobFieldLabel: {
        fontSize: 10
    },
    jobFieldContent: {
        fontSize: 12,
        minHeight: 'auto'
    },
    blockContactContainer: {
        padding: 15
    },
    feedBackHeading: {
        fontSize: 16,
        marginTop: 16,
        marginBottom: 16,
        fontFamily: 'SourceSansPro-SemiBold',
        paddingLeft: 15,
        paddingRight: 15,
    },
    noJobsText: {
        color: 'rgba(158, 171, 204, 1)',
        textAlign: 'center',
        paddingRight: 15,
        paddingLeft: 15,
    },
    backButton: {
        flexDirection: 'row',
        alignItems: 'center',
        display: 'none',
        paddingRight: 15,
        paddingLeft: 15,
        marginTop: 10
    },
    backButtonText: {
        color: '#0065DB',
        marginLeft: 5
    },
    '@media (max-width: 1000)': {
        backButton: {
            display: 'flex'
        }
    },
});
